import Vue from 'vue'
import { get } from 'lodash'
import * as vuex from 'vuex'

// Service
import { Customer, Revenue, Ledger, Invoice, Expense, Receipt, Payee, IMeter, Meter, Voucher } from '../services/models'
import source from '@/services/datasource'

interface State {
  customers: Customer[]
  payees: Payee[]
  ledgers: Ledger[]
  meters: Meter[]
  vouchers: Voucher[],
}

export const state: State = {
  customers: [],
  payees: [],
  ledgers: [],
  meters: [],
  vouchers: [],
}

export const getters = {
  customers: (st: State): Customer[] => {
    return get(st, 'customers', [])
  },
  payees: (st: State): Payee[] => {
    return get(st, 'payees', [])
  },
  ledgers: (st: State): Ledger[] => {
    return get(st, 'ledgers', [])
  },
  revenues: (st: State): Revenue[] => {
    return get(st, 'ledgers', []).filter((o: Ledger) => o instanceof Revenue).map((o) => o as any)
  },
  expenses: (st: State): Expense[] => {
    return get(st, 'ledgers', []).filter((o: Ledger) => o instanceof Expense).map((o) => o as any)
  },
  invoices: (st: State): Invoice[] => {
    return get(st, 'invoices', [])
  },
  invoice: (st: State) => (invoiceId: string): Invoice|undefined => {
    const invoices: Invoice[] = get(st, 'invoices', [])
    return invoices.find((o) => o.id === invoiceId)
  },
  receipts: (st: State): Receipt[] => {
    return get(st, 'receipts', [])
  },
  receipt: (st: State) => (receiptId: string): Receipt|undefined => {
    const receipts: Receipt[] = get(st, 'receipts', [])
    return receipts.find((o) => o.id === receiptId)
  },
  meters: (st: State): Meter[] => {
    return get(st, 'meters', [])
  },
  meter: (st: State) => (meterId: string): Meter|undefined => {
    const meters: Meter[] = get(st, 'meters', [])
    return meters.find((o) => o.id === meterId)
  },
  vouchers: (st: State): Voucher[] => {
    return get(st, 'vouchers', [])
  },
  voucher: (st: State) => (voucherId: string): Voucher|undefined => {
    const vouchers: Voucher[] = get(st, 'vouchers', [])
    return vouchers.find((o) => o.id === voucherId)
  },
}

export const mutations = {
  customers(st: State, customers: Customer[]) {
    Vue.set(st, 'customers', customers)
  },
  payees(st: State, payees: Payee[]) {
    Vue.set(st, 'payees', payees)
  },
  ledgers(st: State, ledgers: Ledger[]) {
    Vue.set(st, 'ledgers', ledgers)
  },
  revenues(st: State, revenues: Revenue[]) {
    Vue.set(st, 'revenues', revenues)
  },
  invoices(st: State, invoices: Invoice[]) {
    Vue.set(st, 'invoices', invoices)
  },
  receipts(st: State, receipts: Receipt[]) {
    Vue.set(st, 'receipts', receipts)
  },
  meters(st: State, meters: Meter[]) {
    Vue.set(st, 'meters', meters)
  },
  vouchers(st: State, vouchers: Voucher[]) {
    Vue.set(st, 'vouchers', vouchers)
  },
}

export const actions: vuex.ActionTree<State, {}> = {
  async fetchCustomers({ commit }) {
    await source.loggedIn()
    const customers = await source.customers()
    commit('customers', customers)
  },

  async fetchPayees({ commit }) {
    await source.loggedIn()
    const payees = await source.payees()
    commit('payees', payees)
  },

  async fetchLedgers({ commit }) {
    await source.loggedIn()
    const ledgers = await source.ledgers()
    commit('ledgers', ledgers)
  },

  async fetchRevenues({ dispatch }) {
    dispatch('fetchLedgers')
  },

  async fetchInvoices({ commit }) {
    await source.loggedIn()
    const invoices = await source.invoices()
    commit('invoices', invoices)
  },

  async fetchReceipts({ commit }) {
    await source.loggedIn()
    const receipts = await source.receipts()
    commit('receipts', receipts)
  },

  async fetchMeters({ commit }) {
    await source.loggedIn()
    const meters = await source.meters()
    commit('meters', meters)
  },

  async fetchVouchers({ commit }) {
    await source.loggedIn()
    const vouchers = await source.vouchers()
    commit('vouchers', vouchers)
  },

  async writeCustomer({ dispatch }, payload: Customer) {
    await source.writeCustomer(payload)
    dispatch('fetchCustomers')
  },

  async writeRevenue({ dispatch }, payload: Revenue): Promise<any> {
    await source.writeLedger(payload)
    dispatch('fetchLedgers')
  },

  async writeExpense({ dispatch }, payload: Expense): Promise<any> {
    await source.writeLedger(payload)
    dispatch('fetchLedgers')
  },

  async writeInvoice({ dispatch }, payload: Invoice): Promise<any> {
    if (!payload.issueDate) {
      payload.issueDate = new Date()
    }
    if (!payload.code) {
      payload.code = await source.newDocNumber('invoices', payload.issueDate)
    }
    await source.writeInvoice(payload)
    dispatch('fetchInvoices')
  },

  async writeMeter({ dispatch }, payload: Meter): Promise<any> {
    await source.writeMeter(payload)
    dispatch('fetchMeters')
  },

  async writeVoucher({ dispatch }, payload: Voucher): Promise<any> {
    await source.writeVoucher(payload)
    dispatch('fetchVouchers')
  },

  /**
   * Convert existing Invoice => Receipt.
   *
   * @param payload
   */
  async issueReceipt({ dispatch }, payload: { invoice: Invoice, issueDate: Date }): Promise<any> {
    const receipt = new Receipt(payload.invoice)
    receipt.issueDate = payload.issueDate
    receipt.code = await source.newDocNumber('receipts', payload.issueDate)
    // Calculate new document number.
    await dispatch('writeReceipt', receipt)
  },

  async writeReceipt({ dispatch }, payload: Receipt): Promise<any> {
    await source.writeReceipt(payload)
    dispatch('fetchReceipts')
  },

  async deleteReceipt({ dispatch }, receiptId: string): Promise<any> {
    await source.deleteReceipt(receiptId)
    dispatch('fetchReceipts')
  },

  async deleteInvoice({ dispatch }, invoiceId: string): Promise<any> {
    await source.deleteInvoice(invoiceId)
    dispatch('fetchInvoice')
  },

  async deleteLedger({ dispatch }, ledgerId: string): Promise<any> {
    // await source.delete
  },
}
